import axios from 'axios'
import { getEpsBaseUrl } from 'modules/api/eps-base-url'
import type { CustomerConfigKey, CustomerConfigValue } from './types'

/** How long a fetched (or 404-null) config value is reused before refetching. */
const TTL_MS = 60_000

type CacheEntry = { promise: Promise<unknown>; timestamp: number }

const cache = new Map<string, CacheEntry>()

/** Clears the request cache. Exposed for use in tests only. */
export const clearCustomerConfigCache = (): void => {
  cache.clear()
}

/**
 * Fetches a typed customer configuration value from EPS.
 * Returns null when the key is not configured for the customer (endpoint throws 404/error).
 *
 * Results are cached by (clientPublicKey, key) for up to TTL_MS (60s):
 * - In-flight de-duplication: simultaneous callers (e.g. many card instances mounting at once)
 *   share a single outstanding request rather than each firing their own.
 * - A cached value is reused across mounts/navigations until the TTL elapses, then refetched —
 *   so an admin flag change is reflected within ~60s (or immediately on reload).
 * - 404s (key not configured) are cached as null but still expire with the TTL, so a
 *   newly-configured key is picked up within ~60s.
 * - Transient errors (network failures, 5xx) are evicted immediately so a retry can succeed.
 *
 * @param clientPublicKey - The customer's public key
 * @param key - A registered key from CustomerConfigMap
 */
export const getCustomerConfig = <K extends CustomerConfigKey>(
  clientPublicKey: string,
  key: K
): Promise<CustomerConfigValue<K> | null> => {
  const cacheKey = `${clientPublicKey}:${key}`

  const cached = cache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < TTL_MS) {
    return cached.promise as Promise<CustomerConfigValue<K> | null>
  }

  const promise: Promise<CustomerConfigValue<K> | null> = axios
    .get(`${getEpsBaseUrl()}/customers/${clientPublicKey}/configurations/${key}`)
    .then((response) => response.data.value as CustomerConfigValue<K>)
    .catch((error) => {
      const isNotFound = error?.response?.status === 404
      if (!isNotFound) {
        cache.delete(cacheKey)
      }
      return null
    })

  cache.set(cacheKey, { promise, timestamp: Date.now() })
  return promise
}
